import {
  AppSessionCellId,
  AppSessionEventState,
  AppSessionEventStateIndices,
  AppSessionId,
  CellId,
  ExhaustiveAppSessionEvent,
  ExportTypeAndStatus,
  Output,
  OutputContent,
  OutputContentId,
  OutputId,
  asciiCompare,
  createAppSessionEventStateIndices,
  getExportTypesAndStatus,
  notEmpty,
  typedObjectEntries,
  typedObjectFromEntries,
  typedObjectValues,
  updateDraftAppSessionEventState,
} from "@hex/common";
import {
  PayloadAction,
  createDraftSafeSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { omitBy } from "lodash";
import { shallowEqual } from "react-redux";

import { RootState } from "../store";
import { createShallowResultSelector } from "../utils/createShallowSelector";

import {
  OutputContentLoadStatus,
  OutputContentValue,
  outputContentSelectors,
} from "./outputContentSlice";

export type OutputWithContent = Output & {
  outputContents: OutputContent[];
};

export type ResolvedOutputContent = OutputContent & OutputContentValue;

export type OutputWithResolvedContent = Output & {
  contents: ResolvedOutputContent[];
  exportTypesAndStatus: ExportTypeAndStatus[];
};

/**
 * Encapsulates the loading state and metadata for app session even state
 */
export type AppSessionEventSliceValue = {
  numberOfSubscribers: number;
} & (
  | {
      type: "initial";
      initialRevision: undefined;
      state: undefined;
      indices: undefined;
      selectors: undefined;
    }
  | {
      type: "loading";
      initialRevision: undefined;
      state: undefined;
      indices: undefined;
      selectors: undefined;
    }
  | {
      type: "error";
      initialRevision: undefined;
      state: undefined;
      indices: undefined;
      selectors: undefined;
    }
  | {
      type: "success";
      initialRevision: number;
      state: AppSessionEventState;
      indices: AppSessionEventStateIndices;
      selectors: AppSessionEventStateSelectors;
    }
);

const makeInitialValue = (): AppSessionEventSliceValue => ({
  type: "initial",
  initialRevision: undefined,
  state: undefined,
  selectors: undefined,
  indices: undefined,
  numberOfSubscribers: 0,
});

/**
 * A mapping of app session id to app session event state, its loading state, and useful metadata
 */
export interface AppSessionEventSliceState {
  [appSessionId: AppSessionId]: AppSessionEventSliceValue | undefined;
}

const initialAppSessionEventSliceState: AppSessionEventSliceState = {};

const appSessionEventSlice = createSlice({
  name: "appSessionEvent",
  initialState: initialAppSessionEventSliceState,
  reducers: {
    subscribe: (
      state,
      {
        payload: { appSessionId },
      }: PayloadAction<{ appSessionId: AppSessionId }>,
    ) => {
      const prev = (state[appSessionId] ??= makeInitialValue());
      prev.numberOfSubscribers++;
    },
    unsubscribe: (
      state,
      {
        payload: { appSessionId },
      }: PayloadAction<{ appSessionId: AppSessionId }>,
    ) => {
      const prev = state[appSessionId];
      if (prev != null) {
        prev.numberOfSubscribers--;
        // evict if there are no more subscribers
        if (prev.numberOfSubscribers === 0) {
          delete state[appSessionId];
        }
      }
    },
    setLoading: (
      state,
      {
        payload: { appSessionId },
      }: PayloadAction<{
        appSessionId: AppSessionId;
      }>,
    ) => {
      const prev = (state[appSessionId] ??= makeInitialValue());
      prev.type = "loading";
    },
    setError: (
      state,
      {
        payload: { appSessionId },
      }: PayloadAction<{
        appSessionId: AppSessionId;
      }>,
    ) => {
      const prev = (state[appSessionId] ??= makeInitialValue());
      prev.type = "error";
    },
    setSuccess: (
      state,
      {
        payload: { appSessionId, initialRevision, initialState },
      }: PayloadAction<{
        appSessionId: AppSessionId;
        initialRevision: number;
        initialState: AppSessionEventState;
      }>,
    ) => {
      const prev = (state[appSessionId] ??= makeInitialValue());
      prev.type = "success";
      prev.initialRevision = initialRevision;
      prev.state = initialState;
      prev.selectors = createAppSessionEventStateSelectors(appSessionId);
      prev.indices = createAppSessionEventStateIndices(initialState);
    },
    handleEvents: (
      state,
      {
        payload: { appSessionId, events },
      }: PayloadAction<{
        appSessionId: AppSessionId;
        events: readonly ExhaustiveAppSessionEvent[];
      }>,
    ) => {
      const prev = state[appSessionId];
      if (prev == null || prev.type !== "success") {
        throw new Error(
          `Event state not found for app session ${appSessionId}`,
        );
      } else {
        updateDraftAppSessionEventState({
          state: prev.state,
          indices: prev.indices,
          events,
        });
      }
    },
  },
});

export const appSessionEventSliceReducer = appSessionEventSlice.reducer;

export const appSessionEventSliceActions = appSessionEventSlice.actions;

export const appSessionEventSliceSelectors = {
  select: (state: RootState) => state.appSessionEvent,
  selectValue: (state: RootState, appSessionId: AppSessionId) =>
    appSessionEventSliceSelectors.select(state)[appSessionId],
  selectValueOrThrow: (state: RootState, appSessionId: AppSessionId) => {
    const value = appSessionEventSliceSelectors.selectValue(
      state,
      appSessionId,
    );

    if (value == null) {
      throw new Error(
        `Event slice value not found for app session ${appSessionId}`,
      );
    } else {
      return value;
    }
  },
  selectLoadedValueOrThrow: (state: RootState, appSessionId: AppSessionId) => {
    const value = appSessionEventSliceSelectors.selectValueOrThrow(
      state,
      appSessionId,
    );

    if (value.type !== "success") {
      throw new Error(
        `Event slice value state is '${value.type}' for app session ${appSessionId}`,
      );
    } else {
      return value;
    }
  },
  selectEventState: (state: RootState, appSessionId: AppSessionId) =>
    appSessionEventSliceSelectors.selectValue(state, appSessionId)?.state,
  selectEventStateOrThrow: (state: RootState, appSessionId: AppSessionId) =>
    appSessionEventSliceSelectors.selectLoadedValueOrThrow(state, appSessionId)
      .state,
  selectIndices: (state: RootState, appSessionId: AppSessionId) =>
    appSessionEventSliceSelectors.selectValue(state, appSessionId)?.indices,
  selectIndicesOrThrow: (state: RootState, appSessionId: AppSessionId) =>
    appSessionEventSliceSelectors.selectLoadedValueOrThrow(state, appSessionId)
      .indices,
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- we infer the result
function createAppSessionEventStateSelectors(appSessionId: AppSessionId) {
  const root = {
    safeSelect: (state: RootState) =>
      appSessionEventSliceSelectors.selectValue(state, appSessionId)?.state,
    select: (state: RootState) =>
      appSessionEventSliceSelectors.selectEventStateOrThrow(
        state,
        appSessionId,
      ),
    selectCellIdToAppSessionCellId: (state: RootState) =>
      appSessionEventSliceSelectors.selectIndices(state, appSessionId)
        ?.cellIdToAppSessionCellId ?? {},
    selectAppSessionCellIdToOutputIds: (state: RootState) =>
      appSessionEventSliceSelectors.selectIndices(state, appSessionId)
        ?.appSessionCellIdToOutputIds ?? {},
    selectOutputIdToOutputContentIds: (state: RootState) =>
      appSessionEventSliceSelectors.selectIndices(state, appSessionId)
        ?.outputIdToOutputContentIds ?? {},
  };

  const appSession = {
    safeSelect: (state: RootState) => root.safeSelect(state)?.appSession,
    select: (state: RootState) => root.select(state).appSession,
  };

  const selectAppSessionCells = (state: RootState) =>
    root.safeSelect(state)?.appSessionCells;

  const appSessionCells = {
    select: selectAppSessionCells,
    selectArray: createDraftSafeSelector(selectAppSessionCells, (entities) =>
      entities != null
        ? typedObjectValues(entities).sort((a, b) => asciiCompare(a.id, b.id))
        : null,
    ),
    selectById: (state: RootState, appSessionCellId: AppSessionCellId) =>
      appSessionCells.select(state)?.[appSessionCellId],
    selectByCellId: (state: RootState, cellId: CellId) => {
      const appSessionCellId =
        root.selectCellIdToAppSessionCellId(state)[cellId];
      if (appSessionCellId == null) {
        return undefined;
      } else {
        return appSessionCells.selectById(state, appSessionCellId);
      }
    },
  };

  const stableOutputContentsCache = new WeakMap<Output, OutputContent[]>();
  const getStableOutputContents = (
    output: Output,
    contents: OutputContent[],
  ): OutputContent[] => {
    const prevVal = stableOutputContentsCache.get(output);

    if (prevVal != null && shallowEqual(prevVal, contents)) {
      return prevVal;
    } else {
      stableOutputContentsCache.set(output, contents);
      return contents;
    }
  };

  const stableOutputWithContentCache = new WeakMap<
    Output,
    WeakMap<OutputContent[], OutputWithContent>
  >();
  const getStableOutputWithContent = (
    output: Output,
    outputContents: OutputContent[],
  ): OutputWithContent => {
    const stableOutputContents = getStableOutputContents(
      output,
      outputContents,
    );

    let innerCache = stableOutputWithContentCache.get(output);
    if (innerCache == null) {
      innerCache = new WeakMap();
      stableOutputWithContentCache.set(output, innerCache);
    }

    const prevVal = innerCache.get(stableOutputContents);

    if (prevVal != null) {
      return prevVal;
    } else {
      const newVal = {
        ...output,
        outputContents: stableOutputContents,
      };
      innerCache.set(stableOutputContents, newVal);
      return newVal;
    }
  };

  const selectOutputs = (state: RootState) => root.safeSelect(state)?.outputs;

  const selectOutputContents = (state: RootState) =>
    root.safeSelect(state)?.outputContents;

  const selectOutputsWithContent = createDraftSafeSelector(
    selectOutputs,
    selectOutputContents,
    root.selectOutputIdToOutputContentIds,
    (outputs, outputContents, index) =>
      typedObjectFromEntries(
        typedObjectEntries(outputs ?? {}).map(([outputId, output]) => [
          outputId,
          output != null
            ? getStableOutputWithContent(
                output,
                index[outputId]
                  ?.map((cId) => outputContents?.[cId])
                  .filter(notEmpty) ?? [],
              )
            : undefined,
        ]),
      ),
  );

  const outputsWithContent = {
    select: selectOutputsWithContent,
    selectArray: createDraftSafeSelector(
      selectOutputsWithContent,
      (entities) =>
        entities != null
          ? typedObjectValues(entities)
              .filter(notEmpty)
              .sort((a, b) => asciiCompare(a.id, b.id))
          : null,
    ),
    selectById: (state: RootState, outputId: OutputId) =>
      outputsWithContent.select(state)?.[outputId],
    createSelectByAppSessionCellId: (appSessionCellId: AppSessionCellId) =>
      createShallowResultSelector(
        selectOutputsWithContent,
        (state: RootState) =>
          root.selectAppSessionCellIdToOutputIds(state)[appSessionCellId],
        (entities, outputIds) =>
          outputIds
            ?.map((id) => entities?.[id])
            .filter(notEmpty)
            .sort((a, b) => a.order - b.order) ?? [],
      ),
  };

  const stableResolvedOutputContentCache = new WeakMap<
    OutputContent,
    WeakMap<OutputContentValue, ResolvedOutputContent>
  >();
  const getStableResolvedOutputContent = (
    outputContent: OutputContent,
    contentValue: OutputContentValue,
  ): ResolvedOutputContent => {
    let innerCache = stableResolvedOutputContentCache.get(outputContent);
    if (innerCache == null) {
      innerCache = new WeakMap();
      stableResolvedOutputContentCache.set(outputContent, innerCache);
    }

    const prevVal = innerCache.get(contentValue);

    if (prevVal != null) {
      return prevVal;
    } else {
      const newVal = {
        ...outputContent,
        ...contentValue,
      };
      innerCache.set(contentValue, newVal);
      return newVal;
    }
  };

  const stableResolvedOutputContentsCache = new WeakMap<
    OutputWithContent,
    ResolvedOutputContent[]
  >();
  const getStableResolvedOutputContents = (
    output: OutputWithContent,
    contents: ResolvedOutputContent[],
  ): ResolvedOutputContent[] => {
    const prevVal = stableResolvedOutputContentsCache.get(output);

    if (prevVal != null && shallowEqual(prevVal, contents)) {
      return prevVal;
    } else {
      stableResolvedOutputContentsCache.set(output, contents);
      return contents;
    }
  };

  const stableOutputWithResolvedContentCache = new WeakMap<
    OutputWithContent,
    WeakMap<ResolvedOutputContent[], OutputWithResolvedContent>
  >();
  const getStableOutputWithResolvedContent = (
    output: OutputWithContent,
    outputContentValues: Record<
      OutputContentId,
      OutputContentValue | undefined
    >,
  ): OutputWithResolvedContent => {
    const resolvedOutputContents = output.outputContents.map((c) =>
      getStableResolvedOutputContent(
        c,
        outputContentValues[c.id] ?? {
          id: c.id,
          status: OutputContentLoadStatus.NO_CONTENT,
          contents: undefined,
        },
      ),
    );
    const stableOutputContents = getStableResolvedOutputContents(
      output,
      resolvedOutputContents,
    );

    let innerCache = stableOutputWithResolvedContentCache.get(output);
    if (innerCache == null) {
      innerCache = new WeakMap();
      stableOutputWithResolvedContentCache.set(output, innerCache);
    }

    const prevVal = innerCache.get(stableOutputContents);

    if (prevVal != null) {
      return prevVal;
    } else {
      const newVal = {
        ...output,
        contents: stableOutputContents,
        exportTypesAndStatus: stableOutputContents.every(
          (c) => c.status === "READY",
        )
          ? getExportTypesAndStatus(stableOutputContents)
          : [],
      };
      innerCache.set(stableOutputContents, newVal);
      return newVal;
    }
  };

  const selectOutputsWithResolvedContent = createDraftSafeSelector(
    selectOutputsWithContent,
    (state: RootState) =>
      outputContentSelectors.selectAppSessionValueOrThrow(state, appSessionId)
        .outputContents,
    (
      outputs,
      outputContents,
    ): Record<OutputId, OutputWithResolvedContent | undefined> =>
      typedObjectFromEntries(
        typedObjectEntries(outputs ?? {}).map(([outputId, output]) => [
          outputId,
          output != null
            ? getStableOutputWithResolvedContent(output, outputContents)
            : undefined,
        ]),
      ),
  );

  const outputsWithResolvedContent = {
    select: selectOutputsWithResolvedContent,
    selectArray: createDraftSafeSelector(
      selectOutputsWithResolvedContent,
      (entities) =>
        entities != null
          ? typedObjectValues(entities)
              .filter(notEmpty)
              .sort((a, b) => asciiCompare(a.id, b.id))
          : null,
    ),
    selectById: (state: RootState, outputId: OutputId) =>
      outputsWithResolvedContent.select(state)?.[outputId],
    createSelectByAppSessionCellId: (appSessionCellId: AppSessionCellId) =>
      createShallowResultSelector(
        selectOutputsWithResolvedContent,
        (state: RootState) =>
          root.selectAppSessionCellIdToOutputIds(state)[appSessionCellId],
        (entities, outputIds) =>
          outputIds
            ?.map((id) => entities?.[id])
            .filter(notEmpty)
            .sort((a, b) => a.order - b.order) ?? [],
      ),
  };

  const selectScopeItemsWithHidden = (state: RootState) =>
    root.safeSelect(state)?.scopeItems;
  const selectScopeItems = createDraftSafeSelector(
    selectScopeItemsWithHidden,
    (entities) =>
      entities == null ? null : omitBy(entities, (value) => value?.isHidden),
  );

  const scopeItems = {
    select: selectScopeItems,
    selectWithHidden: selectScopeItemsWithHidden,
    selectArray: createDraftSafeSelector(selectScopeItems, (entities) =>
      entities != null
        ? typedObjectValues(entities).sort((a, b) =>
            asciiCompare(a.name, b.name),
          )
        : null,
    ),
    selectByName: (state: RootState, name: string) =>
      selectScopeItemsWithHidden(state)?.[name],
  };

  return {
    ...root,
    appSession,
    appSessionCells,
    outputsWithContent,
    outputsWithResolvedContent,
    scopeItems,
  };
}
type AppSessionEventStateSelectors = ReturnType<
  typeof createAppSessionEventStateSelectors
>;

export function appSessionEventStateSelectors(
  appSessionId: AppSessionId,
): AppSessionEventStateSelectors {
  const wrapSafeSelector = <
    F extends (state: RootState, ...otherArgs: any[]) => any,
  >(
    getSelector: (rawSelectors: AppSessionEventStateSelectors) => F,
  ): F =>
    ((state: RootState, ...otherArgs: any[]) => {
      const value = appSessionEventSliceSelectors.selectValue(
        state,
        appSessionId,
      );

      if (value?.type !== "success") {
        return null;
      }

      const rawSelector = getSelector(
        appSessionEventSliceSelectors.selectLoadedValueOrThrow(
          state,
          appSessionId,
        ).selectors,
      ) as any;

      return rawSelector(state, ...otherArgs);
    }) as F;

  const wrapSafeSelectorCreator = <
    F extends (
      ...args: any[]
    ) => (state: RootState, ...otherArgs: any[]) => any,
  >(
    getSelectorCreator: (rawSelectors: AppSessionEventStateSelectors) => F,
  ): F =>
    ((...args: any[]) =>
      (state: RootState, ...otherArgs: any[]) => {
        const value = appSessionEventSliceSelectors.selectValue(
          state,
          appSessionId,
        );

        if (value?.type !== "success") {
          return null;
        }

        const rawSelectorCreator = getSelectorCreator(
          appSessionEventSliceSelectors.selectLoadedValueOrThrow(
            state,
            appSessionId,
          ).selectors,
        ) as any;

        return rawSelectorCreator(...args)(state, ...otherArgs);
      }) as F;

  const wrapSelector = <
    F extends (state: RootState, ...otherArgs: any[]) => any,
  >(
    getSelector: (rawSelectors: AppSessionEventStateSelectors) => F,
  ): F =>
    ((state: RootState, ...otherArgs: any[]) => {
      const rawSelector = getSelector(
        appSessionEventSliceSelectors.selectLoadedValueOrThrow(
          state,
          appSessionId,
        ).selectors,
      ) as any;

      return rawSelector(state, ...otherArgs);
    }) as F;

  return {
    safeSelect: wrapSafeSelector((s) => s.safeSelect),
    select: wrapSelector((s) => s.select),
    selectCellIdToAppSessionCellId: wrapSafeSelector(
      (s) => s.selectCellIdToAppSessionCellId,
    ),
    selectAppSessionCellIdToOutputIds: wrapSafeSelector(
      (s) => s.selectAppSessionCellIdToOutputIds,
    ),
    selectOutputIdToOutputContentIds: wrapSafeSelector(
      (s) => s.selectOutputIdToOutputContentIds,
    ),
    appSession: {
      safeSelect: wrapSafeSelector((s) => s.appSession.safeSelect),
      select: wrapSelector((s) => s.appSession.select),
    },
    appSessionCells: {
      select: wrapSafeSelector((s) => s.appSessionCells.select),
      selectArray: wrapSafeSelector((s) => s.appSessionCells.selectArray),
      selectById: wrapSafeSelector((s) => s.appSessionCells.selectById),
      selectByCellId: wrapSafeSelector((s) => s.appSessionCells.selectByCellId),
    },
    outputsWithContent: {
      select: wrapSafeSelector((s) => s.outputsWithContent.select),
      selectArray: wrapSafeSelector((s) => s.outputsWithContent.selectArray),
      selectById: wrapSafeSelector((s) => s.outputsWithContent.selectById),
      createSelectByAppSessionCellId: wrapSafeSelectorCreator(
        (s) => s.outputsWithContent.createSelectByAppSessionCellId,
      ),
    },
    outputsWithResolvedContent: {
      select: wrapSafeSelector((s) => s.outputsWithResolvedContent.select),
      selectArray: wrapSafeSelector(
        (s) => s.outputsWithResolvedContent.selectArray,
      ),
      selectById: wrapSafeSelector(
        (s) => s.outputsWithResolvedContent.selectById,
      ),
      createSelectByAppSessionCellId: wrapSafeSelectorCreator(
        (s) => s.outputsWithResolvedContent.createSelectByAppSessionCellId,
      ),
    },
    scopeItems: {
      select: wrapSafeSelector((s) => s.scopeItems.select),
      selectWithHidden: wrapSafeSelector((s) => s.scopeItems.selectWithHidden),
      selectArray: wrapSafeSelector((s) => s.scopeItems.selectArray),
      selectByName: wrapSafeSelector((s) => s.scopeItems.selectByName),
    },
  };
}
